import { PeriodFilters, initialPeriodFilters } from './PeriodList';
import { ViewRange } from './types';

export type SavedView = {
  runId: string;
  periodId?: string;
  filters: PeriodFilters;
  viewport?: ViewRange | null;
};
const KEY = 'pm.market-explorer.view.v1';
const validRange = (value?: ViewRange | null) =>
  value &&
  Number.isFinite(value.min) &&
  Number.isFinite(value.max) &&
  value.max > value.min
    ? value
    : null;

export const readSavedView = (): SavedView | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const persisted = JSON.parse(
      window.localStorage.getItem(KEY) || 'null',
    ) as SavedView | null;
    const runId = params.get('run') || persisted?.runId;
    if (!runId || !/^[a-zA-Z0-9-]{1,80}$/.test(runId)) return null;
    const sameRun = persisted?.runId === runId;
    const samePeriod =
      !params.has('period') || params.get('period') === persisted?.periodId;
    const min = Number(params.get('from'));
    const max = Number(params.get('to'));
    const queryFilters = Object.fromEntries(
      ['query', 'kind', 'horizon', 'sort', 'scope']
        .filter(key => params.has(key))
        .map(key => [key, params.get(key) || '']),
    );
    return {
      runId,
      periodId:
        params.get('period') || (sameRun ? persisted?.periodId : undefined),
      filters: {
        ...initialPeriodFilters,
        ...(sameRun ? persisted?.filters || {} : {}),
        ...queryFilters,
      },
      viewport: validRange(
        params.has('from') && params.has('to')
          ? { min, max }
          : sameRun && samePeriod
            ? persisted?.viewport
            : null,
      ),
    };
  } catch {
    return null;
  }
};

export const saveView = (view: SavedView) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(view));
    const params = new URLSearchParams(window.location.search);
    params.set('run', view.runId);
    Object.entries(view.filters).forEach(([key, value]) => {
      if (value && value !== initialPeriodFilters[key as keyof PeriodFilters])
        params.set(key, value);
      else params.delete(key);
    });
    if (view.periodId) params.set('period', view.periodId);
    else params.delete('period');
    if (validRange(view.viewport)) {
      params.set('from', String(Math.round(view.viewport!.min)));
      params.set('to', String(Math.round(view.viewport!.max)));
    } else {
      params.delete('from');
      params.delete('to');
    }
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}${window.location.hash}`,
    );
  } catch {
    /* Browsers can disable local persistence; the active view still works. */
  }
};
